import { useEffect, useState } from 'react';
import { useTeams } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { Modal } from '../components/ui';

/**
 * Chooses the team, or teams, a batch of leads is spread across.
 *
 * Distribution used to resolve the team implicitly — "the one team you manage"
 * — which meant an admin who manages nothing got told they manage nothing, and
 * a manager running two teams got told to pass a team_id the UI could not send.
 * Making the choice explicit is the whole point of this dialog.
 *
 * Members are pooled across the chosen teams and rotated as one list, so the
 * split is even per person, not per team.
 */
export function DistributeModal({
  count,
  busy,
  onClose,
  onConfirm,
}: {
  count: number;
  busy: boolean;
  onClose: () => void;
  onConfirm: (teamIds: string[]) => void;
}) {
  const { user } = useAuth();
  const { data: teams = [], isPending } = useTeams();

  // Org-wide scope distributes into any active team; anyone else, only the
  // teams they are recorded as managing. The server enforces this too.
  const choices = teams.filter(
    (t) => t.is_active && (user?.scope === 'all' || t.manager?.id === user?.id),
  );
  const usable = choices.filter((t) => t.member_count > 0);

  const [selected, setSelected] = useState<string[]>([]);

  // A manager with a single team should not have to tick it.
  useEffect(() => {
    if (usable.length === 1) setSelected([usable[0]!.id]);
  }, [usable.length]);

  function toggle(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const allSelected = usable.length > 0 && usable.every((t) => selected.includes(t.id));

  return (
    <Modal
      title={`Distribute ${count} lead${count === 1 ? '' : 's'}`}
      onClose={onClose}
      width={520}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={selected.length === 0 || busy}
            onClick={() => onConfirm(selected)}
          >
            {busy ? <span className="spinner" /> : 'Distribute evenly'}
          </button>
        </>
      }
    >
      {isPending ? (
        <p className="hint" style={{ margin: 0 }}>Loading teams…</p>
      ) : choices.length === 0 ? (
        <p style={{ margin: 0 }}>
          {user?.scope === 'all'
            ? 'No active teams yet. Create one on the Teams page first.'
            : 'You do not manage a team, so there is nowhere to distribute these leads.'}
        </p>
      ) : (
        <>
          <div className="team-choice-head">
            <span>{usable.length > 1 ? 'Choose one or more teams' : 'Distributing into'}</span>
            {usable.length > 1 && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setSelected(allSelected ? [] : usable.map((t) => t.id))}
              >
                {allSelected ? 'Clear all' : 'Select all teams'}
              </button>
            )}
          </div>

          <div className="team-choices">
            {choices.map((team) => {
              const empty = team.member_count === 0;
              return (
                <label
                  key={team.id}
                  className={`team-choice${empty ? ' is-empty' : ''}`}
                  title={empty ? 'This team has no members to receive leads' : undefined}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(team.id)}
                    disabled={empty}
                    onChange={() => toggle(team.id)}
                  />
                  <span className="team-choice-body">
                    <strong>{team.name}</strong>
                    <span className="team-choice-meta">
                      {team.manager?.full_name
                        ? `Managed by ${team.manager.full_name}`
                        : 'No manager'}
                      {' · '}
                      {empty
                        ? 'no members'
                        : `${team.member_count} member${team.member_count === 1 ? '' : 's'}`}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>

          <p className="hint">
            Members of every chosen team are pooled and take turns, so each person gets
            roughly the same number of leads. A team's manager is never a recipient.
          </p>
        </>
      )}
    </Modal>
  );
}
